import { z } from 'zod';
import { isHttpUrl } from '@os/shared';
import type { RichDoc, RichNode } from './rich';

// zod 4 would probe `Function('')` to compile its object parsers: the production CSP refuses it and reports a
// violation on every page that reads a board (P12). Interpreted parsers, set before any schema is used.
z.config({ jitless: true });

export const SCHEMA_VERSION = 3;
export const WORLD_LIMIT = 1_000_000;

const num = z.number().finite();
const point = z.object({ x: num, y: num });

const common = {
  id: z.string().min(1),
  parentId: z.string().nullable(),
  x: num,
  y: num,
  w: num,
  h: num,
  rotation: num,
  z: z.string().min(1),
  locked: z.boolean(),
  style: z.record(z.string(), z.unknown()),
  /** Who added the object and when (P5d); optional, and a bad value is dropped rather than hiding the object. */
  createdBy: z.string().min(1).max(64).optional().catch(undefined),
  createdAt: z.number().int().nonnegative().optional().catch(undefined),
  /** The page the object was put on (P14); absent on boards written before pages, read as the first page. */
  pageId: z.string().min(1).max(64).optional().catch(undefined),
};

export const SHAPES = ['rectangle', 'ellipse', 'diamond', 'triangle', 'star', 'bubble'] as const;
export const CONTAINER_TYPES = ['section', 'group'] as const;
export const ROUTINGS = ['straight', 'elbow', 'curved'] as const;
export const CAPS = ['none', 'arrow'] as const;
export const EMBED_PROVIDERS = ['youtube', 'vimeo', 'drive'] as const;
export const TABLE_MAX_ROWS = 100;
export const TABLE_MAX_COLS = 26;
/** Fixed row height of tables (Ruling 9): a table's height is rows × this. */
export const TABLE_ROW_H = 40;
export const DIAGRAM_MAX_SOURCE = 20_000;
/** Task lists (P16-5): limits, and the fixed geometry their height follows (like tables, Ruling 4). */
export const TASK_MAX_ITEMS = 200;
export const TASK_MAX_TEXT = 1000;
export const TASK_MAX_TITLE = 200;
export const TASK_MAX_INDENT = 2;
export const TASK_HEADER_H = 56;
export const TASK_ROW_H = 36;
export const TASK_ROW_H_COMPACT = 26;
export const TASK_FOOTER_H = 44;
export const TASK_MIN_W = 200;
export const TASK_MAX_W = 800;
/** A list's height: header, one fixed-height row per task, footer (Ruling 4). */
export const taskListHeight = (count: number, compact: boolean): number =>
  TASK_HEADER_H + Math.max(0, count) * (compact ? TASK_ROW_H_COMPACT : TASK_ROW_H) + TASK_FOOTER_H;

const richNode: z.ZodType<RichNode> = z.lazy(() =>
  z.object({
    type: z.string().min(1),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(richNode).optional(),
    text: z.string().optional(),
    marks: z.array(z.object({ type: z.string().min(1), attrs: z.record(z.string(), z.unknown()).optional() })).optional(),
  }),
);
export const richDoc: z.ZodType<RichDoc> = z.object({ type: z.literal('doc'), content: z.array(richNode) });
const httpUrl = z.string().refine(isHttpUrl, 'http(s) URL expected');
const unit = num.min(0).max(1);
const connectorEnd = z.object({ objectId: z.string().min(1).nullable(), anchor: z.object({ nx: unit, ny: unit }).nullable(), x: num, y: num });
export type ConnectorEnd = z.infer<typeof connectorEnd>;
const id = z.string().min(1).max(64);
const taskItem = z.object({
  id,
  text: z.string().catch('').transform((t) => t.slice(0, TASK_MAX_TEXT)),
  done: z.boolean().catch(false),
  indent: z.number().int().min(0).max(TASK_MAX_INDENT).catch(0),
  assignee: id.nullable().catch(null),
  doneBy: id.optional().catch(undefined),
  doneAt: z.number().int().nonnegative().optional().catch(undefined),
});
export type TaskItem = z.infer<typeof taskItem>;
/** A bad task is left out and a list too long after a merge is cut: the list itself never disappears. */
const taskItems = z.array(z.unknown()).transform((raw) =>
  raw.flatMap((r) => {
    const t = taskItem.safeParse(r);
    return t.success ? [t.data] : [];
  }).slice(0, TASK_MAX_ITEMS),
);

export const objectSchema = z.discriminatedUnion('type', [
  z.object({ ...common, type: z.literal('image'), assetId: z.string(), crop: z.object({ x: num, y: num, w: num, h: num }), alt: z.string() }),
  // Text types: `text` is the plain text of `content` (or the stored text of a pre-v2 object without a fragment).
  z.object({ ...common, type: z.literal('text'), text: z.string(), content: richDoc.nullable().optional(), fontSize: num }),
  z.object({ ...common, type: z.literal('sticky'), text: z.string(), content: richDoc.nullable().optional(), color: z.string() }),
  z.object({ ...common, type: z.literal('shape'), shape: z.enum(SHAPES), text: z.string().optional(), content: richDoc.nullable().optional() }),
  z.object({ ...common, type: z.literal('arrow'), points: z.array(point).min(2) }),
  z.object({ ...common, type: z.literal('section'), name: z.string().max(200), fill: z.string(), clip: z.boolean(), collapsed: z.boolean() }),
  z.object({ ...common, type: z.literal('group') }),
  z.object({
    ...common, type: z.literal('connector'), start: connectorEnd, end: connectorEnd,
    routing: z.enum(ROUTINGS), startCap: z.enum(CAPS), endCap: z.enum(CAPS),
    // P5b, optional (older documents have neither): midpoint offset in chord lengths, text on the line.
    bend: num.min(-2).max(2).optional(), label: z.string().max(200).optional(),
  }),
  z.object({
    ...common, type: z.literal('link'), url: httpUrl, title: z.string(), description: z.string(), siteName: z.string(),
    thumbAssetId: z.string().nullable(), status: z.enum(['pending', 'ready', 'failed']),
  }),
  z.object({
    ...common, type: z.literal('embed'), url: httpUrl, provider: z.enum(EMBED_PROVIDERS), embedId: z.string().regex(/^[A-Za-z0-9_-]{1,100}$/),
    title: z.string(), thumbAssetId: z.string().nullable(), embeddable: z.boolean().nullable(),
  }),
  z.object({
    ...common, type: z.literal('table'),
    cols: z.array(z.object({ id, w: num })).min(1).max(TABLE_MAX_COLS),
    rows: z.array(z.object({ id, cells: z.record(z.string(), z.string()) })).min(1).max(TABLE_MAX_ROWS),
  }),
  z.object({ ...common, type: z.literal('diagram'), source: z.string().max(DIAGRAM_MAX_SOURCE) }),
  // A page link (P14): a live miniature of another page of the board, which it opens.
  z.object({ ...common, type: z.literal('pagelink'), targetPageId: z.string().min(1).max(64) }),
  z.object({
    ...common, type: z.literal('tasklist'),
    title: z.string().catch('').transform((t) => t.slice(0, TASK_MAX_TITLE)),
    compact: z.boolean().catch(false),
    items: taskItems,
  }),
]);

export type ObjectData = z.infer<typeof objectSchema>;
export type ObjectType = ObjectData['type'];

/** Longer than a capped list allows: zod 3 reads every item before checking `.max()` (GHSA-mrhx-c7xc-m3w5, P12a). */
function overCap(raw: unknown): boolean {
  if (typeof raw !== 'object' || raw === null) return false;
  const o = raw as { type?: unknown; rows?: unknown; cols?: unknown };
  if (o.type !== 'table') return false;
  return (Array.isArray(o.rows) && o.rows.length > TABLE_MAX_ROWS) || (Array.isArray(o.cols) && o.cols.length > TABLE_MAX_COLS);
}

/**
 * Never throws: an invalid object is reported as null so the UI can skip it instead of crashing. Also run by the
 * board's room on what clients wrote, so an oversized table is refused on its length before zod reads it.
 */
export function parseObject(raw: unknown): ObjectData | null {
  if (overCap(raw)) return null;
  const r = objectSchema.safeParse(raw);
  if (!r.success) return null;
  // A task list's size follows its (valid, capped) tasks and stays within its width bounds, whoever wrote w/h last.
  if (r.data.type === 'tasklist') {
    const w = Math.min(TASK_MAX_W, Math.max(TASK_MIN_W, r.data.w));
    return { ...r.data, w, h: taskListHeight(r.data.items.length, r.data.compact) };
  }
  return r.data;
}
